import React, {
  createContext,
  useContext,
  type PropsWithChildren,
} from 'react';
import { useQuery } from '@tanstack/react-query';

// ─── Types ─────────────────────────────────────────────────────────────────

/**
 * The 12 capability flags defined by the community roles system.
 * Mirrors the CommunityRole content type on the CMS server.
 *
 * See: cms-server/docs/community-roles/COMMUNITY_ROLES.md
 */
export interface CommunityPermissions {
  canViewAnnouncements: boolean;
  canManageAnnouncements: boolean;
  canViewProperties: boolean;
  canManageProperties: boolean;
  canViewEvents: boolean;
  canManageEvents: boolean;
  canViewTickets: boolean;
  canCreateTickets: boolean;
  canManageTickets: boolean;
  canViewBudget: boolean;
  canManageBudget: boolean;
  canViewGL: boolean;
  canManageGL: boolean;
  canPostGL: boolean;
  canCloseGLPeriod: boolean;
  canExportGLReports: boolean;
  canViewAssessments: boolean;
  canManageAssessments: boolean;
  canManageInvites: boolean;
  canViewVendors: boolean;
  canManageVendors: boolean;
  canViewWorkOrders: boolean;
  canManageWorkOrders: boolean;
  canViewStr: boolean;
  canManageStr: boolean;
  canViewDirectory: boolean;
  canManageDirectory: boolean;
  canManageRoles: boolean;
  canManageBilling: boolean;
  canManageFormTemplates: boolean;
}

export type PermissionFlag = keyof CommunityPermissions;

/** Community role names considered management-level (can skip wizard, access dashboard). */
export const MANAGEMENT_ROLES = new Set(['Super Admin', 'Admin', 'Manager']);

interface PermissionsState {
  /** Merged permission flags for the active community. null = not yet resolved. */
  permissions: CommunityPermissions | null;
  /** True while the membership query is in flight. */
  isLoading: boolean;
  /** Whether the permissions query is enabled (requires activeCommunityId to be set). */
  permissionsEnabled: boolean;
  /** Names of the community roles the current user holds in the active community. */
  roleNames: string[];
  /**
   * Returns true if the user holds the given permission.
   *
   * Rules:
   *  - While loading → true (avoid hiding UI elements before data arrives)
   *  - No custom roles assigned → true (user is likely HOA / Manager;
   *    the server enforces real access — this context is UI-only)
   *  - Has custom roles → checks the merged capability flags
   */
  hasPermission: (flag: PermissionFlag) => boolean;
}

// ─── Utilities ─────────────────────────────────────────────────────────────

/**
 * Merges an array of role memberships into a single CommunityPermissions map.
 * A flag is true if ANY assigned role grants it (union / least-restrictive merge).
 */
export function mergeCapabilities(memberships: any[]): CommunityPermissions {
  const base: CommunityPermissions = {
    canViewAnnouncements: false,
    canManageAnnouncements: false,
    canViewProperties: false,
    canManageProperties: false,
    canViewEvents: false,
    canManageEvents: false,
    canViewTickets: false,
    canCreateTickets: false,
    canManageTickets: false,
    canViewBudget: false,
    canManageBudget: false,
    canViewGL: false,
    canManageGL: false,
    canPostGL: false,
    canCloseGLPeriod: false,
    canExportGLReports: false,
    canViewAssessments: false,
    canManageAssessments: false,
    canManageInvites: false,
    canViewVendors: false,
    canManageVendors: false,
    canViewWorkOrders: false,
    canManageWorkOrders: false,
    canViewStr: false,
    canManageStr: false,
    canViewDirectory: false,
    canManageDirectory: false,
    canManageRoles: false,
    canManageBilling: false,
    canManageFormTemplates: false
  };

  for (const { communityRole } of memberships) {
    if (!communityRole) continue;
    for (const key of Object.keys(base) as PermissionFlag[]) {
      if (communityRole[key] === true) base[key] = true;
    }
  }

  return base;
}

// ─── Context ───────────────────────────────────────────────────────────────

const PermissionsContext = createContext<PermissionsState>({
  permissions: null,
  isLoading: false,
  permissionsEnabled: false,
  roleNames: [],
  hasPermission: () => true,
});

// ─── Provider ──────────────────────────────────────────────────────────────

interface PermissionsProviderProps extends PropsWithChildren {
  /** TanStack Query key — include all values the query depends on. */
  queryKey: unknown[];
  /** Whether the query should run (e.g. !!token && !!communityId). */
  enabled: boolean;
  /** Async function that resolves to the raw memberships array from the API. */
  queryFn: () => Promise<any[]>;
}

/**
 * Generic PermissionsProvider. Fetches role memberships via the supplied
 * `queryFn` and exposes a merged permission set to all descendant components.
 *
 * Place this inside <QueryClientProvider>. Supply a thin wrapper component
 * that wires in your auth context and GraphQL client.
 *
 * @example
 * function MyAppPermissionsProvider({ children }: PropsWithChildren) {
 *   const { token, communityId } = useAuth();
 *   return (
 *     <PermissionsProvider
 *       queryKey={['permissions', communityId]}
 *       enabled={!!token && !!communityId}
 *       queryFn={() => fetchMyRoles(token, communityId)}
 *     >
 *       {children}
 *     </PermissionsProvider>
 *   );
 * }
 */
export function PermissionsProvider({ children, queryKey, enabled, queryFn }: PermissionsProviderProps) {
  const { data: memberships, isPending } = useQuery({
    queryKey,
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
    queryFn,
  });

  const permissions =
    memberships && memberships.length > 0
      ? mergeCapabilities(memberships)
      : null;

  const roleNames: string[] = (memberships ?? []).map((m: any) => m.communityRole?.name).filter(Boolean);

  const isLoading = isPending;

  const hasPermission = (flag: PermissionFlag): boolean => {
    if (isLoading) return true;
    if (permissions === null) return true;
    return permissions[flag];
  };

  return (
    <PermissionsContext.Provider value={{ permissions, isLoading, permissionsEnabled: enabled, roleNames, hasPermission }}>
      {children}
    </PermissionsContext.Provider>
  );
}

// ─── Hook ──────────────────────────────────────────────────────────────────

/**
 * Returns the permissions state for the active community.
 *
 * @example
 * const { hasPermission, permissions, isLoading } = usePermissions();
 *
 * if (hasPermission('canManageTickets')) {
 *   // show admin actions
 * }
 */
export function usePermissions(): PermissionsState {
  return useContext(PermissionsContext);
}

// ─── Can component ─────────────────────────────────────────────────────────

interface CanProps {
  /** The permission flag to check. */
  permission: PermissionFlag;
  /** Content to render when the user has the permission. */
  children: React.ReactNode;
  /** Optional fallback to render when the user does NOT have permission. */
  fallback?: React.ReactNode;
}

/**
 * Declarative permission gate. Renders `children` when the user holds
 * the given permission; renders `fallback` (default: nothing) otherwise.
 *
 * @example
 * <Can permission="canManageTickets">
 *   <DeleteButton />
 * </Can>
 *
 * <Can permission="canViewBudget" fallback={<div>No budget access</div>}>
 *   <BudgetSummary />
 * </Can>
 */
export function Can({ permission, children, fallback = null }: CanProps) {
  const { hasPermission } = usePermissions();
  return hasPermission(permission) ? <>{children}</> : <>{fallback}</>;
}

// ─── hoa-portal-specific provider ─────────────────────────────────────────
// Wraps PermissionsProvider with the WorkspaceContext + axios GraphQL client
// so call sites don't need to wire up queryFn manually.

import axiosServices from 'utils/axios';
import { useWorkspace } from 'contexts/WorkspaceContext';

const GET_USER_ROLES_QUERY = `
  query GetUserRolesInCommunity($communityId: String!) {
    myRolesInCommunity(communityId: $communityId) {
      documentId
      communityRole {
        documentId
        name
        canViewAnnouncements
        canManageAnnouncements
        canViewDirectory
        canManageDirectory
        canViewProperties
        canManageProperties
        canViewTickets
        canCreateTickets
        canManageTickets
        canViewBudget
        canManageBudget
        canViewGL
        canManageGL
        canPostGL
        canCloseGLPeriod
        canExportGLReports
        canViewAssessments
        canManageAssessments
        canManageInvites
        canManageRoles
        canManageBilling
        canViewEvents
        canManageEvents
        canViewVendors
        canManageVendors
        canViewWorkOrders
        canManageWorkOrders
        canViewStr
        canManageStr
        canManageFormTemplates
      }
    }
  }
`;

/**
 * Drop-in PermissionsProvider for hoa-portal.
 * Reads activeCommunityId from WorkspaceContext and fetches roles via axios.
 *
 * Must be rendered inside <WorkspaceProvider> and <QueryClientProvider>.
 *
 * @example
 * <WorkspaceProvider>
 *   <HoaPermissionsProvider>
 *     <Outlet />
 *   </HoaPermissionsProvider>
 * </WorkspaceProvider>
 */
export function HoaPermissionsProvider({ children }: PropsWithChildren) {
  const { activeCommunityId } = useWorkspace();
  const token = localStorage.getItem('serviceToken');

  return (
    <PermissionsProvider
      queryKey={['permissions', 'v2', activeCommunityId]}
      enabled={!!token && !!activeCommunityId}
      queryFn={async () => {
        const res = await axiosServices.post('/graphql', {
          query: GET_USER_ROLES_QUERY,
          variables: { communityId: activeCommunityId },
        });
        return res.data?.data?.myRolesInCommunity ?? [];
      }}
    >
      {children}
    </PermissionsProvider>
  );
}
